// Mirrors backend com.kervolt.oms.dataclass.bank.cheque.* and ...guarantee.* DTOs 1:1 (wave 3, lane H).

export type ChequeDirection = 'RECEIVED' | 'ISSUED';
export type ChequeStatus =
  | 'RECEIVED' | 'DEPOSITED' | 'CLEARED' | 'BOUNCED' | 'RETURNED' | 'CANCELLED'
  | 'ISSUED' | 'RELEASED' | 'STOPPED';

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const CHEQUE_STATUS_COLORS: Record<ChequeStatus, ChipColor> = {
  RECEIVED: 'default', DEPOSITED: 'info', CLEARED: 'success', BOUNCED: 'error', RETURNED: 'warning',
  CANCELLED: 'default', ISSUED: 'default', RELEASED: 'info', STOPPED: 'warning',
};

export interface Cheque {
  id: string;
  institutionId: string;
  direction: ChequeDirection;
  chequeNumber: string;
  chequeDate: string;
  amount: number;
  status: ChequeStatus;
  partyName?: string | null;
  drawerBank?: string | null;
  bankAccountId?: string | null;
  bankAccountLabel?: string | null;
  chequeBookId?: string | null;
  customerId?: string | null;
  salesInvoiceId?: string | null;
  supplierId?: string | null;
  supplierBillId?: string | null;
  receiptId?: string | null;
  supplierPaymentId?: string | null;
  journalEntryId?: string | null;
  reversalJournalEntryId?: string | null;
  depositedDate?: string | null;
  releasedDate?: string | null;
  clearedDate?: string | null;
  presentedCount: number;
  bouncedDate?: string | null;
  bounceReason?: string | null;
  bounceChargeAmount?: number | null;
  bounceChargeJournalEntryId?: string | null;
  collectionsFollowUp: boolean;
  stopReason?: string | null;
  printedAt?: string | null;
  remarks?: string | null;
  postDated: boolean;
}

export interface ChequePage {
  content: Cheque[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface ReceivedChequeCreateDTO {
  institutionId: string;
  chequeNumber: string;
  chequeDate: string;
  amount: number;
  drawerBank: string;
  customerId?: string;
  salesInvoiceId?: string;
  partyName?: string;
  remarks?: string;
}

export interface IssuedChequeCreateDTO {
  institutionId: string;
  chequeBookId: string;
  chequeNumber?: string;
  chequeDate: string;
  amount: number;
  partyName?: string;
  supplierId?: string;
  supplierBillId?: string;
  remarks?: string;
}

export interface ChequeBounceDTO {
  bounceDate?: string;
  reason: string;
  chargeAmount?: number;
  chargeAccountId?: string;
  segmentId?: string;
}

export interface ChequeBook {
  id?: string;
  institutionId: string;
  bankAccountId: string;
  bookLabel: string;
  leafFrom: number;
  leafTo: number;
  numberWidth?: number;
  receivedDate?: string | null;
  status?: string;
  remarks?: string | null;
  usedLeaves?: number;
  totalLeaves?: number;
  nextLeaf?: string | null;
}

export interface ChequeLayout {
  id?: string;
  institutionId: string;
  layoutName: string;
  bankName?: string | null;
  pageWidthMm: number;
  pageHeightMm: number;
  dateXMm: number;
  dateYMm: number;
  payeeXMm: number;
  payeeYMm: number;
  amountXMm: number;
  amountYMm: number;
  wordsXMm: number;
  wordsYMm: number;
  wordsMaxWidthMm: number;
  crossing: boolean;
  fontSize: number;
  wordsLanguage: 'EN' | 'BN';
  defaultLayout: boolean;
}

export type GuaranteeType = 'BID' | 'PERFORMANCE' | 'ADVANCE_PAYMENT' | 'UTILITY' | 'CUSTOMS' | 'OTHER';
export const GUARANTEE_TYPES: GuaranteeType[] = ['BID', 'PERFORMANCE', 'ADVANCE_PAYMENT', 'UTILITY', 'CUSTOMS', 'OTHER'];
export type GuaranteeStatus = 'ISSUED' | 'AMENDED' | 'RELEASED' | 'INVOKED' | 'EXPIRED';

export const GUARANTEE_STATUS_COLORS: Record<GuaranteeStatus, ChipColor> = {
  ISSUED: 'info', AMENDED: 'info', RELEASED: 'success', INVOKED: 'error', EXPIRED: 'warning',
};

export interface BankGuaranteeEvent {
  id: string;
  eventType: string;
  eventDate: string;
  amount?: number | null;
  journalEntryId?: string | null;
  notes?: string | null;
}

export interface BankGuarantee {
  id: string;
  institutionId: string;
  bankAccountId: string;
  bankAccountLabel?: string;
  guaranteeNumber: string;
  guaranteeType: GuaranteeType;
  beneficiary: string;
  purpose?: string | null;
  amount: number;
  currencyCode?: string | null;
  issueDate: string;
  expiryDate: string;
  claimExpiryDate?: string | null;
  marginPercent?: number | null;
  marginAmount?: number | null;
  marginAccountId?: string | null;
  marginJournalEntryId?: string | null;
  marginReleaseDate?: string | null;
  marginReleaseJournalEntryId?: string | null;
  commissionAccountId?: string | null;
  status: GuaranteeStatus;
  remarks?: string | null;
  daysToExpiry: number;
  events: BankGuaranteeEvent[];
}

export interface BankGuaranteeCreateDTO {
  institutionId: string;
  bankAccountId: string;
  guaranteeNumber: string;
  guaranteeType: GuaranteeType;
  beneficiary: string;
  purpose?: string;
  amount: number;
  currencyCode?: string;
  issueDate: string;
  expiryDate: string;
  claimExpiryDate?: string;
  marginPercent?: number;
  marginAmount?: number;
  marginAccountId?: string;
  commissionAccountId?: string;
  remarks?: string;
}

export interface BankDueItem {
  sourceKey: string;
  documentId: string;
  documentRef: string;
  dueDate: string;
  amount: number;
  currencyCode: string;
  title: string;
  deepLink: string;
  severity: 'INFO' | 'WARNING' | 'OVERDUE';
}
